import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Input,
    Button,
    FormControl,
    FormLabel,
    VStack,
    Text,
    Container,
    Center,
    Heading,
    InputGroup,
    InputLeftElement,
    Alert,
    AlertIcon,
    ScaleFade,
    useColorModeValue,
    Divider,
    Icon,
    Flex,
    FormHelperText,
    useToast,
    keyframes
} from "@chakra-ui/react";
import { EmailIcon, LockIcon } from "@chakra-ui/icons";
import { sendEmail } from "../Slice/PasswordResetRequest/PasswordResetRequestSlice";
import bg from "../image/image1.png";

// Animation keyframes
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const EmailForm = () => {
    const [email, setEmail] = useState("");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const toast = useToast();

    const { loading, success, error } = useSelector((state) => state.email);

    // Theme colors
    const bgColor = useColorModeValue("white", "gray.800");
    const cardBgGradient = useColorModeValue(
        "linear(to-br, white, gray.50)",
        "linear(to-br, gray.800, gray.900)"
    );
    const primaryColor = useColorModeValue("blue.500", "blue.300");
    const headingColor = useColorModeValue("blue.600", "blue.300");
    const textColor = useColorModeValue("gray.600", "gray.300");
    const iconBgColor = useColorModeValue("blue.50", "blue.900");

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(sendEmail(email));

        toast({
            title: "Processing request",
            description: "We're sending a reset code to your email",
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "top"
        });
    };

    // Redirect on successful email send
    useEffect(() => {
        if (success) {
            navigate("/password-reset", { state: { email } });
        }
    }, [success, navigate, email]);

    return (
        <div className="min-h-screen maindiv flex items-center justify-center px-4 py-20 font-p">
            <div className="flex bg-white shadow-2xl rounded-lg overflow-hidden max-w-6xl  w-full">

                <div className="w-full md:w-1/2 ">
                    <Center h="full">
                        <ScaleFade initialScale={0.9} in={true}>
                            <Box
                                p={8}
                                borderRadius="2xl"
                                // boxShadow="2xl"
                                // bgGradient={cardBgGradient}
                                width="100%"
                                // maxWidth="480px"
                                // borderWidth="1px"
                                borderColor={useColorModeValue("gray.200", "gray.700")}
                                position="relative"
                                overflow="hidden"
                                // _before={{
                                //     content: '""',
                                //     position: "absolute",
                                //     top: 0,
                                //     left: 0,
                                //     width: "100%",
                                //     height: "8px",
                                //     bgGradient: "linear(to-r, blue.400, purple.500, pink.400)"
                                // }}
                            >
                                <VStack  align="center">
                                    {/* Lock Icon with animation */}
                                    <Box
                                        p={5}
                                        borderRadius="full"
                                        bg={iconBgColor}
                                        boxShadow="md"
                                        animation={`${float} 3s ease-in-out infinite`}
                                    >
                                        <Icon
                                            as={LockIcon}
                                            w={10}
                                            h={10}
                                            color={primaryColor}
                                        />
                                    </Box>

                                    <Heading size="lg" textAlign="center" color={headingColor} fontWeight="bold" letterSpacing="tight">
                                        Reset Your Password
                                    </Heading>

                                    <Text color={textColor} textAlign="center" fontSize="md" maxW="sm">
                                        Enter your email address below and we'll send you a code to reset your password.
                                    </Text>

                                    <Divider />

                                    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                                        <VStack spacing={3} width="full">
                                            <FormControl>
                                                <FormLabel fontWeight="medium">Email Address</FormLabel>
                                                <InputGroup size="lg">
                                                    <InputLeftElement
                                                        pointerEvents="none"
                                                        children={<EmailIcon color="gray.400" />}
                                                    />
                                                    <Input
                                                        type="email"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        required
                                                        placeholder="Enter your email"
                                                        borderRadius="lg"
                                                        bg={useColorModeValue("white", "gray.700")}
                                                        borderColor={useColorModeValue("gray.300", "gray.600")}
                                                        _hover={{
                                                            borderColor: useColorModeValue("gray.400", "gray.500")
                                                        }}
                                                        _focus={{
                                                            borderColor: "blue.400",
                                                            boxShadow: "0 0 0 1px blue.400"
                                                        }}
                                                    />
                                                </InputGroup>
                                                <FormHelperText color={textColor}>
                                                    We'll send a verification code to this email
                                                </FormHelperText>
                                            </FormControl>

                                            <Button
                                                type="submit"
                                                colorScheme="blue"
                                                isLoading={loading}
                                                loadingText="Sending..."
                                                size="lg"
                                                width="full"
                                                height="60px"
                                                _hover={{
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "xl",
                                                    bgGradient: "linear(to-r, blue.500, blue.600)"
                                                }}
                                                bgGradient="linear(to-r, blue.400, blue.500)"
                                                transition="all 0.3s"
                                                borderRadius="lg"
                                                fontWeight="bold"
                                                fontSize="md"
                                            >
                                                Send Reset Code
                                            </Button>
                                        </VStack>
                                    </form>

                                    {error && (
                                        <Alert status="error" borderRadius="lg" variant="left-accent">
                                            <AlertIcon />
                                            <Text fontSize="sm">{error}</Text>
                                        </Alert>
                                    )}

                                    {success && (
                                        <Alert status="success" borderRadius="lg" variant="left-accent">
                                            <AlertIcon />
                                            <Text fontSize="sm">Email sent successfully! Redirecting...</Text>
                                        </Alert>
                                    )}

                                    <Flex justify="center" width="full" pt={2}>
                                        <Button
                                            variant="link"
                                            color="blue.500"
                                            size="sm"
                                            onClick={() => navigate("/login")}
                                            fontWeight="medium"
                                        >
                                            Back to Login
                                        </Button>
                                    </Flex>
                                </VStack>
                            </Box>
                        </ScaleFade>
                    </Center>

                </div>
                <div className="hidden md:block md:w-1/2">
                    <img
                        src={bg || "/placeholder.svg"}
                        className="object-cover h-full w-full rounded-lg"
                        alt="Background"
                    />
                </div>
            </div>
        </div>
    );
};

export default EmailForm;